<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, computed, onMounted, onUnmounted, getCurrentInstance, watch } from 'vue';
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { useNotification } from "@kyvg/vue3-notification";
import DynamicService from '@/services/DynamicService'
import { mediaPulseModel } from '@/components/Composable/mediaPulse'
import { buildStreamPreviewSpecs } from '@/Utils/previewHost'
import Spinner from "@/components/ui/Spinner.vue"
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import StreamPlayerDynamic from '@/components/DynamicPlayer/StreamPlayerDynamic.vue'
import StreamPlayerHlsDynamic from '@/components/DynamicPlayer/StreamPlayerHlsDynamic.vue'
import DynamicPlayerInterface from '@/components/DynamicPlayer/DynamicPlayerInterface.vue'
import DynamicPlayerSecurity from '@/components/DynamicPlayer/DynamicPlayerSecurity.vue'
import DynamicHeader from '@/components/DynamicPlayer/DynamicHeader.vue'
import DynamicEmbed from '@/components/DynamicPlayer/DynamicEmbed.vue'
import IconBase from '../../components/icon/IconBase.vue';
import FormButton from '../../components/Atoms/FormButton.vue';
import Alert from '../../components/Atoms/Alert.vue';
import IconError from '../../components/icon/IconError.vue';
import TabGroup from '../../components/Atoms/TabGroup.vue';
import TabItem from '../../components/Atoms/TabItem.vue';
import AddDynamicPlayerSourceModal from '../../components/modals/AddDynamicPlayerSourceModal.vue';
import IconChevronDown from '../../components/icon/IconChevronDown.vue';
import IconCamera from '../../components/icon/IconCamera.vue';
import IconFilm from '../../components/icon/IconFilm.vue';
import IconFolder from '../../components/icon/IconFolder.vue';
import { track, EVENTS } from '@/lib/tracking'

const store = useStore()
const router = useRouter()
const route = useRoute()
const instance = getCurrentInstance();
const root = instance.proxy.$root;
const { notify } = useNotification()

const dynamicNavRef = ref(null)
const scopeAlive = ref(true)
const streamPreviewSpecs = ref(null)
const streamPreviewSpecsUrl = ref(null)
const streamPreviewSpecsType = ref(null)
const isAddDynamicPlayerSourceModalVisible = ref(false)

// Media Pulse 
const { subscribeMediaPulse, unsubscribeMediaPulse, mediaPulse } = mediaPulseModel()

// Computed Getters
const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])
const hasLiveSubscription = computed(() => store.getters['User/hasLiveSubscription'])
const hasVodSubscription = computed(() => store.getters['User/hasVodSubscription'])
const player = computed(() => store.getters['DynamicPlayers/dynamicPlayer'])
const players = computed(() => store.getters['DynamicPlayers/dynamicPlayers'])
const playerLoaded = computed(() => store.getters['DynamicPlayers/dynamicPlayerLoaded'])
const allInOneStreams = computed(() => store.getters['Streams/allInOneStreams'])

// Computed 
const playerUrl = computed(() => {
	if (player.value?.source?.liveParent) {
		const range = `${player.value.subSource?.data?.from}-${player.value.subSource?.data?.duration}`
		return `${process.env.VUE_APP_PLAYER_APP_BASE_URL}/${player.value.source.key}?range=${range}&preview=true&abr=${player.value.subSource?.data?.abr ? 'true' : 'false'}`
			+ (player.value.subSource?.data?.namedHls ? '&namedHls=true' : '')
			+ (player.value.subSource?.data?.cap ? '&cap=true' : '');
	} else if (player.value?.subSource) {
		const playerUri = `${process.env.VUE_APP_PLAYER_APP_BASE_URL}/vod/${player.value.subSource?.data?.id}?preview=true`
		return playerUri
	} else return null
})

const sourceLiveStream = computed(() => {
	if (player.value?.source && !player.value?.subSource) {
		return allInOneStreams.value.find(stream => stream._id === player.value.source._id)
	} else return null
})


const playerSourceType = computed(() => {
	if (player.value?.source && !player.value?.subSource) {
		return 'LiveStream'
	} else if (player.value?.subSource && player.value?.source?.liveParent) {
		return 'LiveToVod'
	} else if (player.value?.subSource && !player.value?.source?.liveParent) {
		return 'Vod'
	} else return null
})

const playerSourceIcon = computed(() => {
	if (playerSourceType.value === 'LiveStream') return IconCamera
	if (playerSourceType.value === 'LiveToVod') return IconFilm
	if (playerSourceType.value === 'Vod') return IconFilm
	return null
})

const computedSourceLabel = computed(() => {
	if (player.value?.source) return player.value.source.name
	return 'Choose source'
})

// Watchers
watch(sourceLiveStream, async () => {
	if (sourceLiveStream.value) {
		unsubscribeMediaPulse()
		subscribeMediaPulse(sourceLiveStream.value)
	}
})

watch(mediaPulse, async () => {
	await onMediaPulseChanged()
})

// Hooks
onMounted(async () => {
	// `Ui/loadingStatus` defaults to true and is cleared per-view. Every peer
	// detail/dashboard view clears it; without this a deep-link / refresh onto a
	// Dynamic Player detail would sit on the global spinner forever.
	try {
		await store.dispatch('DynamicPlayers/getPlayers');
		try {
			await store.dispatch('DynamicPlayers/getPlayer', route?.params?.playerId);
		} catch (err) {
			console.log(err)
			notify({
				group: 'error',
				text: 'Failed to load dynamic player data',
			})
			await router.push({ name: "ChannelList" });
			return
		}
		await store.dispatch('Streams/getStreams')
		subscribeMediaPulse(sourceLiveStream.value)
	} finally {
		store.dispatch('Ui/setLoadingStatus', false)
	}
})

onUnmounted(() => {
	scopeAlive.value = false
	unsubscribeMediaPulse()
})

// Methods
const onClickAnalytics = () => {
	let id = '';
	if (playerSourceType.value === 'Vod') {
		id = player.value.subSource.data.id;
	} else {
		id = player.value.source?.key;
	}
	if (id) {
		const route = router.resolve({ name: 'AnalyticsDashboardWithRoute', params: { videoId: id } });
		window.open(route.href, '_blank');
	}
}

const onDeletePlayer = async () => {
	const playerName = player.value.name
	try {
		await DynamicService.deletePlayer(
			player.value._id
		)
		await store.dispatch('DynamicPlayers/getPlayers', true);
		notify({
			group: 'success',
			text: `Dynamic Player ${playerName} is completely deleted from your account`,
		})
		router.push({
			name: "DynamicDashboard",
		})
	} catch (err) {
		console.log(err)
		notify({
			group: 'error',
			title: "Couldn't delete dynamic player",
			text: err.message
		});
	}
}

const setElement = async (item) => {
	try {
		if (item?.file && item?.folder) {
			await DynamicService.setVodPlayer(
				player.value._id,
				player.value.name,
				item.folder._id,
				item.file._id
			)
			track(EVENTS.DYNAMIC_PLAYER_SOURCE_ASSIGNED, { playerId: player.value._id, sourceType: 'vod' })
			notify({
				group: 'success',
				text: `Source set`,
			})
		} else if (item?.type === 'live') {
			await DynamicService.setLiveStreamPlayer(
				player.value._id,
				player.value.name,
				item._id
			)
			track(EVENTS.DYNAMIC_PLAYER_SOURCE_ASSIGNED, { playerId: player.value._id, sourceType: 'live' })
			notify({
				group: 'success',
				text: `Source set`,
			})
		} else {
			await DynamicService.setLiveStreamPlayer(
				player.value._id,
				player.value.name,
				null
			)
			notify({
				group: 'success',
				text: `Source removed`,
			})
		}
		// The player resolves the source behind this key on every page load and
		// caches the answer; without this the Direct Player Link keeps serving
		// the previous source until that entry ages out.
		DynamicService.clearPlayerMetadataCache(player.value._id, player.value.key)
		await store.dispatch('DynamicPlayers/getPlayers', true);
		await store.dispatch('DynamicPlayers/getPlayer', route?.params?.playerId);
	} catch (err) {
		console.log(err)
	}
}


const onMediaPulseChanged = async () => {
	const specs = buildStreamPreviewSpecs(
		mediaPulse.value,
		sourceLiveStream.value?.key,
		sourceLiveStream.value
	);
	streamPreviewSpecs.value = specs;
	streamPreviewSpecsType.value = specs.type || null;
	streamPreviewSpecsUrl.value = specs.url || null;
}


const changeRouteQuery = (item) => {
	router.replace({ query: { ...route.query, tab: item } })
}


const showDeleteModal = () => {
	showModal('delete-player');
}


const showAddPlayerModal = () => {
	dynamicNavRef.value?.showAddPlayerModal();
}

const showAddDynamicPlayerSourceModal = () => {
	isAddDynamicPlayerSourceModalVisible.value = true
}


</script>

<template>
	<div class="w-full container detail-shell px-4 mt-4 lg:pt-8 lg:self-start">
		<Spinner v-if="loadingStatus" text="Retrieving data..." classes="text-surface-8 mt-24"
			spinner-color="var(--c-surface-8)" spinner-size="15px" />
		<main class="grid grid-cols-1 lg:grid-cols-12 gap-4" v-else>
			<DynamicHeader class="lg:col-span-12" @delete="showDeleteModal" @add-player="showAddPlayerModal" />
			<div class="lg:col-span-6">

				<Alert
					v-if="hasLiveSubscription && !hasLiveSubscription.enabled || !hasLiveSubscription && hasVodSubscription && !hasVodSubscription.enabled"
					class="mb-2" type="error" hideIcon>
					<icon-base class="!mr-2">
						<IconError />
					</icon-base>
					<span>Your plan has ended. Please <a class="underline" target="_blank" href="/app/subscribe">upgrade</a>
						to
						continue.</span>
				</Alert>
				<div v-if="!playerLoaded"
					class="video-placeholder test-base font-medium text-surface-4 flex items-center justify-center flex-wrap bg-surface-darker">
					<Spinner text="Retrieving data..." classes="text-surface-8" spinner-color="var(--c-surface-8)"
						spinner-size="15px" />
				</div>
				<div v-else-if="player && !player.enabled"
					class="video-placeholder test-base font-medium text-surface-4 flex items-center justify-center flex-wrap bg-surface-darker">
					<p class="text-surface-8 text-base w-full text-center mb-2 mt-auto">
						Disabled Stream
					</p>
					<p class="text-surface-6 text-sm w-full text-center mb-auto">Please enable</p>
				</div>
				<div v-else>
					<div v-if="player && !player.source"
						class="video-placeholder test-base font-medium text-surface-4 flex content-center items-center justify-center flex-wrap bg-surface-2 relative">
						<h5 class="text-surface-text-2 text-base font-medium basis-full text-center !mb-2">No source selected yet
						</h5>
						<p class="text-surface-8 text-xs basis-full text-center mt-0 max-w-64 lg:max-w-none">Select a Live Stream or Video Hosting source
							to preview your player in real time.</p>
					</div>
					<iframe v-else-if="playerUrl" :src="playerUrl" class="embed video-frame" frameborder="0" width="100%"
						scrolling="no" allowfullscreen webkitallowfullscreen mozallowfullscreen oallowfullscreen
						msallowfullscreen />
					<div v-if="sourceLiveStream && !mediaPulse || sourceLiveStream && !mediaPulse.alive"
						class="video-placeholder test-base font-medium text-surface-4 flex content-center items-center justify-center flex-wrap bg-surface-darker">
						<h5 class="text-surface-text-2 text-base font-medium basis-full text-center !mb-2">Source is offline</h5>
						<p class="text-surface-8 text-xs basis-full text-center mt-0 max-w-64 lg:max-w-none">Your selected source isn’t live right now. The preview starts automatically once it goes live.</p>
					</div>
					<stream-player-dynamic
						v-else-if="sourceLiveStream && mediaPulse && streamPreviewSpecsUrl && streamPreviewSpecsType === 'mse'"
						:stream="sourceLiveStream" :stream-url="streamPreviewSpecsUrl" :media-pulse="mediaPulse"
						class="video-wrapper bg-surfacedarker flex justify-center items-center" />

					<stream-player-hls-dynamic
						v-else-if="sourceLiveStream && mediaPulse && streamPreviewSpecsUrl && streamPreviewSpecsType === 'hls'"
						:stream="sourceLiveStream" :stream-url="streamPreviewSpecsUrl" :autoplay="true" :frameless="true"
						class="video-wrapper" />
				</div>


				<DynamicEmbed class="!mt-4" />
				<FormButton type="secondary" class="!hidden lg:!flex w-full justify-center mt-3"
					:disabled="!player.subSource && !player.source" @click="onClickAnalytics">View your video analytics
				</FormButton>

			</div>
			<div class="lg:col-span-6">
				<section class="bg-surface-2 rounded-xl shadow-[0_2px_6px_rgba(0,0,0,0.28)] p-4 md:p-5 !mb-4">
					<div class="flex justify-between items-center">
						<h6 class="text-surface-text-2 text-base font-medium !mb-2 flex items-center gap-x-2">
							Source
							<div class="rounded-full" :class="[player?.source ? 'bg-success-light size-2' : 'bg-error-light size-1.5', mediaPulse && mediaPulse.alive ? '!size-1 animate-ping animate-duration-1000 animate-ease-linear animate-normal animate-fill-both' : '']">
							</div>
						</h6>
						<FormButton v-if="player && player.source" type="light" size="xs" class="!mb-1"
							@click="setElement(null)">Remove</FormButton>
					</div>
					<FormButton
						customClasses="text-tiny bg-surface-3 text-surface-text-2 hover:bg-surface-4 hover:text-surface-9 !rounded-md h-8 !p-2 flex items-center justify-between w-full !mb-2 truncate gap-x-2"
						@click="showAddDynamicPlayerSourceModal">
						<icon-base v-if="playerSourceIcon" :class="mediaPulse && mediaPulse.alive ? 'text-success-light' : 'text-surface-8'"><component :is="playerSourceIcon" /></icon-base>
						<span class="truncate">{{computedSourceLabel}}</span>
						<icon-base class="!ml-auto"><icon-folder /></icon-base>
					</FormButton>
					<p class="text-surface-7 text-tiny mt-2" v-if="!player.source && !player.subSource">Start by selecting a source above</p>
				</section>
				<TabGroup class="grid grid-cols-2 !mb-4">
					<TabItem centered label="Settings" :isActive="!route.query.tab || route.query?.tab === 'interface'"
						@click="changeRouteQuery('interface')" />
					<TabItem centered label="Security" :isActive="route.query.tab === 'security'"
						@click="changeRouteQuery('security')" />
				</TabGroup>
				<DynamicPlayerInterface
					v-if="!route.query.tab || ['interface', 'functions', 'captions'].includes(route.query?.tab)" />
				<DynamicPlayerSecurity v-if="route.query?.tab === 'security'" />
				<FormButton type="secondary" class="!flex lg:!hidden w-full justify-center mt-4"
					:disabled="!player.subSource && !player.source" @click="onClickAnalytics">View your video analytics
				</FormButton>
			</div>
		</main>
		<AddDynamicPlayerSourceModal v-model:is-visible="isAddDynamicPlayerSourceModalVisible"
			ref="addDynamicPlayerSourceModalRef" @add-stream="setElement" />
		<confirm-modal modalId="delete-player" message="Are you sure you want to delete your Dynamic Player?"
			modal-type="danger" @modal-confirm="onDeletePlayer" />
	</div>
</template>

<style scoped></style>
